import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';

import { App } from '@/App';
import { AuthGate } from '@/auth/AuthGate';
import { EnvironmentProvider } from '@/environment/EnvironmentProvider';
import { theme } from '@/theme';
import '@/styles/fonts.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // An admin returning to the tab should see a colleague's change.
      refetchOnWindowFocus: true,
      // Retrying a 4xx just repeats a request the server already refused; only
      // transient failures are worth a second attempt.
      retry: (failureCount, error) => {
        const status = (error as { status?: number }).status ?? 0;
        if (status >= 400 && status < 500) return false;
        return failureCount < 2;
      },
    },
    mutations: { retry: false },
  },
});

const container = document.getElementById('root');
if (!container) throw new Error('#root is missing from index.html');

createRoot(container).render(
  <StrictMode>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <QueryClientProvider client={queryClient}>
        {/* Outside AuthGate: sign-in is always against production regardless of
            the selection (doc 18), but every request AuthGate makes still needs
            an environment on it. */}
        <EnvironmentProvider>
          <AuthGate>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </AuthGate>
        </EnvironmentProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </StrictMode>,
);
